import { useState, useEffect, useCallback, useContext, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Skeleton } from "primereact/skeleton";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import AlertService from "../../../services/AlertService";
import CourseService from "../../../services/CourseService";
import DocNameCard from "../documents/DocNameCard";
import AuthContext from "../../../auth/AuthContext";
import View from "./view";
import { ThemeContext } from "../../../theme/ThemeContext";

if (!Object.groupBy) {
  Object.groupBy = (array, keyFn) =>
    array.reduce((result, item) => {
      const key = keyFn(item);
      if (!result[key]) result[key] = [];
      result[key].push(item);
      return result;
    }, {});
}

const CourseViewDoc = () => {
  const { id: paramCourseId } = useParams();
  const { state } = useLocation();
  const courseId = paramCourseId || state?.id;
  const navigate = useNavigate();

  const authContext = useContext(AuthContext);
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";

  const [selectedDocument, setSelectedDocument] = useState(null);
  const [groupedDocuments, setGroupedDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const activeDocumentRef = useRef(null);
  const cardAbortRef = useRef(null); 

  const courseAccess = authContext.getPermissions("courses");
  if (courseAccess.filter((f) => f.access.includes(1)).length === 0) {
    navigate("/");
  }

const groupDocuments = useCallback((documents) => {
  const filteredDocuments = documents.filter((doc) => doc.status === true);

  const parentGroups = Object.groupBy(filteredDocuments, (d) => d.documentType || "");
  return Object.entries(parentGroups).map(([docType, docs]) => ({
    key: docType,
    visible: true,
    items: docs,
  }));
}, []);

  useEffect(() => {
    if (activeDocumentRef.current) {
      activeDocumentRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [selectedDocument]);

  const toggleVisibility = (key) => {
    const updated = groupedDocuments.map((group) =>
      group.key === key ? { ...group, visible: !group.visible } : group
    );
    setGroupedDocuments(updated);
  };

const loadDocument = useCallback(
    async (id, forceFetch = false, signal) => { 
      if (!id) return;

      setLoading(true);
      try {
        const response = await CourseService.GetAttachments(
          id,
          "",
          authContext,
          signal 
        );

        if (signal?.aborted) return;

        if (response.isSuccess) {
          const data = response.data;
          if (Array.isArray(data)) {
            setGroupedDocuments(groupDocuments(data));
          } else if (data.attachments && data.attachments.length > 0) {
            setSelectedDocument(data);
          }
        } else {
          AlertService.error(response.errorMessage);
        }
      } catch (err) {
        if (err.name === "AbortError") return; 
        AlertService.error("Failed to load documents.");
      } finally {
        if (!signal?.aborted) setLoading(false); 
      }
    },
    [authContext, groupDocuments]
  );

useEffect(() => {
    const controller = new AbortController(); 
    if (courseId) loadDocument(courseId, true, controller.signal); 
    return () => controller.abort(); 
  }, [courseId, loadDocument]);

const handleCardClick = (doc) => {
    setSelectedDocument(doc);
    if (doc?.id) {
      if (cardAbortRef.current) {
        cardAbortRef.current.abort(); 
      }
      const controller = new AbortController();
      cardAbortRef.current = controller;
      loadDocument(doc.id, true, controller.signal);
    }
  };
  return (
    <div className="container-fluid custom-container">
      {!selectedDocument ? (
        loading ? (
          <div className="row mt-4">
            {[...Array(8)].map((_, idx) => (
              <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-4" key={idx}>
                <div
                  className="shadow-sm border-0 rounded-3 p-3"
                  style={{ minHeight: "120px" }}
                >
                  <Skeleton width="60%" height="1.5rem" className="mb-2" />
                  <Skeleton width="30%" height="1rem" className="mb-2" />
                  <Skeleton width="80%" height="1.25rem" />
                </div>
              </div>
            ))}
          </div>
        ) : groupedDocuments.length > 0 ? (
          groupedDocuments.map((group) => (
            <div key={group.key} className="mb-5 mt-5">
              <div className="mb-4">
                <div
                  className={`p-3 mb-3 rounded cursor-pointer ${
                    isDark
                      ? "bg-dark border border-dark text-light"
                      : "bg-light border text-dark"
                  }`}
                  onClick={() => toggleVisibility(group.key)}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-bold">{group.key}</span>
                    <div
                      className={`d-flex align-items-center justify-content-center rounded-circle border ${
                        isDark
                          ? "bg-dark border-light text-white"
                          : "bg-white border text-dark"
                      }`}
                      style={{ width: "28px", height: "28px" }}
                    >
                      {group.visible ? (
                        <FaChevronUp className="fs-6" />
                      ) : (
                        <FaChevronDown className="fs-6" />
                      )}
                    </div>
                  </div>
                </div>

                {group.visible && (
                  <div className="row">
                    {group.items.map((document, ix) => (
                      <div
                        key={document.id || ix}
                        className="col-12 col-sm-6 col-md-4 col-lg-3 mb-4"
                        onClick={() => handleCardClick(document)}
                        style={{ cursor: "pointer" }}
                      >
                        <DocNameCard
                          document={document}
                          no={ix + 1}
                          clickHandler={handleCardClick}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-5 text-muted">No Documents found</div>
        )
      ) : (
        <div className="row g-2 mt-4">
          <div className="col-md-3 col-lg-2">
            <div
              className="pe-2 scroll-no-buttons"
              style={{
                minHeight: "80vh",
                maxHeight: "80vh",
                overflowY: "auto",
              }}
            >
              {groupedDocuments.map((group) => (
                <div key={group.key}>
                  <h6 className="text-center fw-semibold mb-2">{group.key}</h6>
                  {group.items.map((document, ix) => {
                    const isActive =
                      selectedDocument?.documentId === document.documentId;
                    return (
                      <div
                        key={document.id || ix}
                        ref={isActive ? activeDocumentRef : null}
                      >
                        <DocNameCard
                          document={document}
                          no={ix + 1}
                          clickHandler={handleCardClick}
                          isActive={isActive}
                          compact
                        />
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          <div className="col-12 col-md-10">
            <View document={selectedDocument} />
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseViewDoc;
